/**
 * Page-view tracking hook
 *
 * Fires a `page_viewed` PostHog event on every react-router navigation.
 * Mount once at the top of the component tree (inside <BrowserRouter>).
 *
 * We emit an explicit named `page_viewed` event so we can include a human-
 * readable `route_name` alongside the raw `path`.  The built-in
 * `capture_pageview` in main.tsx only fires on the initial load, so this hook
 * captures all subsequent SPA navigations.
 *
 * @changeHistory
 * - 2026-06-22: Initial creation - Route-aware page_viewed tracking (GitHub Copilot)
 * - 2026-07-27: Renamed profile route analytics to team member profile (Copilot)
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { CLIENT_EVENTS, track } from './events';

/** Maps pathname prefixes to human-readable route names. */
function resolveRouteName(pathname: string): string {
  if (pathname === '/' || pathname === '/login') return 'Login';
  if (pathname.startsWith('/dashboard')) return 'Dashboard';
  if (pathname === '/submission') return 'CDA Submission';
  if (pathname.startsWith('/submission/edit')) return 'CDA Edit';
  if (pathname.startsWith('/approval')) return 'Approval Dashboard';
  if (pathname === '/blog-posts') return 'Blog Posts';
  if (pathname === '/blog-posts/create') return 'Blog Post Create';
  if (pathname.startsWith('/blog-posts/edit')) return 'Blog Post Edit';
  if (pathname === '/profile') return 'Team Member Profile';
  if (pathname.startsWith('/agents')) return 'Agent Management';
  if (pathname === '/team') return 'Team Management';
  if (pathname.startsWith('/transactions')) return 'Transactions';
  if (pathname.startsWith('/listings')) return 'Listings';
  if (pathname.startsWith('/plans')) return 'Compensation Plans';
  if (pathname.startsWith('/title-companies')) return 'Title Companies';
  if (pathname.startsWith('/audit')) return 'Audit Logs';
  if (pathname.startsWith('/admin')) return 'Admin';
  if (pathname.startsWith('/status')) return 'Status';
  return pathname;
}

/**
 * Mount once inside <BrowserRouter> to track page views on every navigation.
 */
export function usePageViewTracking(): void {
  const location = useLocation();
  // Store previous path to avoid firing twice on mount alongside capture_pageview
  const prevPathRef = useRef<string | null>(null);

  useEffect(() => {
    const path = `${location.pathname}${location.search}${location.hash}`;

    // Skip the very first render — PostHog's capture_pageview handles the initial load
    if (prevPathRef.current === null) {
      prevPathRef.current = path;
      return;
    }

    // Only fire when the URL actually changes
    if (prevPathRef.current === path) {
      return;
    }

    prevPathRef.current = path;

    track(CLIENT_EVENTS.PAGE_VIEWED, {
      path,
      route_name: resolveRouteName(location.pathname),
    });
  }, [location.pathname, location.search, location.hash]);
}
